import { queryOptions, useQuery, useMutation } from '@tanstack/react-query';
import { getMe, updateUserPassword } from './auth.service';
import { persistStorageService } from './persist-storage';
import { UpdateUserPasswordPayload } from './auth.type';
import { toaster } from '@/components/ui/toaster';
import { useNavigate } from 'react-router-dom';
import { getInvitationAuthTokenKey } from '@/config/constants';

export const authQueryKeys = {
  all: ['auth'] as const,
  me: () => [...authQueryKeys.all, 'me'] as const,
};

const getMeQuery = () => {
  const queryKey = authQueryKeys.me();

  return queryOptions({
    queryKey,
    queryFn: () => getMe(),
    staleTime: 30 * 1000, // 30 seconds
    refetchOnWindowFocus: false,
    gcTime: 30 * 1000,
  });
};

export const useAuthUser = ({ enabled = true }: { enabled?: boolean } = {}) => {
  return useQuery({
    ...getMeQuery(),
    enabled,
    select: (data) => {
      if (data.status === 'success') {
        persistStorageService.setPersistItem({
          name: 'user',
          value: JSON.stringify(data.user),
        });
      }
      return data;
    },
  });
};

// Hook for updating user password during signup completion
export const useUpdateUserPassword = () => {
  const router = useNavigate();
  return useMutation({
    mutationFn: (payload: UpdateUserPasswordPayload) =>
      updateUserPassword(payload),
    onSuccess: () => {
      persistStorageService.batchRemovePersistItems({
        names: ['token', 'refresh_token', 'user', 'expires_at'],
      });
      const key = getInvitationAuthTokenKey();
      if (key) {
        persistStorageService.batchRemovePersistItems({
          names: [key],
        });
      }
      router('/login');
    },
    onError: (error: Error) => {
      toaster.create({
        title: 'Error Setting Password',
        description:
          error.message || 'Failed to set password. Please try again.',
        type: 'error',
        duration: 5000,
      });
    },
  });
};
